/**
 * Instructor V2 API Client
 */

import { apiClient } from '../client';
import type { ApiError } from '@/lib/types/errors';
import { err, ok, type Result } from '@/lib/types/result';
import type {
  InstructorDetailV2,
  InstructorReviewsResponseV2,
  InstructorReviewV2,
} from '@/types/discovery';

/**
 * Get instructor detail by ID
 */
export const getInstructor = async (
  instructorId: string
): Promise<Result<InstructorDetailV2, ApiError>> => {
  try {
    const response = await apiClient.get<InstructorDetailV2>(`/v2/instructors/${instructorId}`);

    if (!response.ok) {
      return err(response.error);
    }

    return ok(response.value);
  } catch (error) {
    return err({
      type: 'network',
      message: error instanceof Error ? error.message : 'Failed to fetch instructor detail',
    });
  }
};

/**
 * Get paginated instructor reviews (cursor-based)
 */
export const getInstructorReviews = async (
  instructorId: string,
  options?: { limit?: number; cursor?: string; sort?: string }
): Promise<Result<InstructorReviewsResponseV2, ApiError>> => {
  try {
    const params = new URLSearchParams();
    if (options?.limit !== undefined) {
      params.append('limit', String(options.limit));
    }
    if (options?.cursor !== undefined) {
      params.append('cursor', options.cursor);
    }
    if (options?.sort !== undefined) {
      params.append('sort', options.sort);
    }

    const queryString = params.toString();
    const endpoint = `/v2/instructors/${instructorId}/reviews${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get<InstructorReviewsResponseV2>(endpoint);

    if (!response.ok) {
      return err(response.error);
    }

    return ok(response.value);
  } catch (error) {
    return err({
      type: 'network',
      message: error instanceof Error ? error.message : 'Failed to fetch instructor reviews',
    });
  }
};

/**
 * Validation schema for review submission. Built lazily via dynamic import:
 * this module sits in the eager chunk (route defs import it for SSR loaders),
 * and a module-scope zod import would pin all of zod (~80KB) into that chunk
 * for every page even though only the review mutations need it.
 */
const getReviewValidationSchema = async () => {
  const { z } = await import('zod');
  return z.object({
    rating: z.number().min(1, 'Rating must be at least 1').max(5, 'Rating must be at most 5'),
    text: z
      .string()
      .min(50, 'Review text must be at least 50 characters')
      .max(1000, 'Review text must not exceed 1000 characters'),
  });
};

/**
 * Create a new review for an instructor
 */
export const createInstructorReview = async (
  instructorId: string,
  review: { rating: number; text: string }
): Promise<Result<InstructorReviewV2, ApiError>> => {
  try {
    // Validate input
    const reviewValidationSchema = await getReviewValidationSchema();
    const validationResult = reviewValidationSchema.safeParse(review);
    if (!validationResult.success) {
      const errorMessages = validationResult.error.issues.map((issue) => issue.message).join(', ');
      return err({
        type: 'validation',
        status: 422,
        message: errorMessages,
      } as const);
    }

    const response = await apiClient.post<InstructorReviewV2>(
      `/v2/instructors/${instructorId}/reviews`,
      {
        rating: review.rating,
        content: review.text, // Transform 'text' to 'content' for backend
      }
    );

    if (!response.ok) {
      // Return generic error - can't check status on ApiError type
      return err(response.error);
    }

    return ok(response.value);
  } catch (error) {
    return err({
      type: 'network',
      message: error instanceof Error ? error.message : 'Failed to submit review',
    });
  }
};

/**
 * Update an existing review
 */
export const updateInstructorReview = async (
  instructorId: string,
  reviewId: string,
  updates: { rating?: number; text?: string }
): Promise<Result<InstructorReviewV2, ApiError>> => {
  try {
    // Validate updates if provided
    if (updates.rating !== undefined || updates.text !== undefined) {
      const reviewValidationSchema = await getReviewValidationSchema();
      const validationResult = reviewValidationSchema.partial().safeParse(updates);
      if (!validationResult.success) {
        const errorMessages = validationResult.error.issues
          .map((issue) => issue.message)
          .join(', ');
        return err({
          type: 'validation',
          status: 422,
          message: errorMessages,
        } as const);
      }
    }

    const response = await apiClient.patch<InstructorReviewV2>(
      `/v2/instructors/${instructorId}/reviews/${reviewId}`,
      {
        ...(updates.rating !== undefined && { rating: updates.rating }),
        ...(updates.text !== undefined && { content: updates.text }), // Transform 'text' to 'content'
      }
    );

    if (!response.ok) {
      // Return generic error - can't check status on ApiError type
      return err(response.error);
    }

    return ok(response.value);
  } catch (error) {
    return err({
      type: 'network',
      message: error instanceof Error ? error.message : 'Failed to update review',
    });
  }
};

/**
 * Delete a review (soft delete)
 */
export const deleteInstructorReview = async (
  instructorId: string,
  reviewId: string
): Promise<Result<void, ApiError>> => {
  try {
    const response = await apiClient.delete<void>(
      `/v2/instructors/${instructorId}/reviews/${reviewId}`
    );

    if (!response.ok) {
      // Return generic error - can't check status on ApiError type
      return err(response.error);
    }

    return ok(undefined);
  } catch (error) {
    return err({
      type: 'network',
      message: error instanceof Error ? error.message : 'Failed to delete review',
    });
  }
};
